/**
 * Login Page — Copilot-style clean layout
 *
 * Back arrow, title, subtitle, Apple/Google buttons,
 * OR divider, email input → navigate to /login/email.
 * Clean cream background (signup owns the hero moment).
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { T, BRAND } from '@/styles/tokens';

const useFadeIn = (delay = 0) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    setTimeout(() => setMounted(true), 100 + delay);
  }, [delay]);

  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(12px)',
    transition: prefersReducedMotion ? 'none' : 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  };
};

function getAuthErrorMessage(err: any): string {
  const code = err.code;
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/user-disabled':
      return 'This account has been disabled';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in cancelled';
    case 'auth/popup-blocked':
      return 'Pop-up blocked. Allow pop-ups and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email';
    case 'auth/invalid-api-key':
      return 'Configuration error. Contact support.';
    default:
      if (err.message?.includes('API key')) {
        return 'Configuration error. Contact support.';
      }
      return 'Sign-in failed. Try again.';
  }
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithGoogle, signInWithApple, profile } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const next = params.get('next') || '/app/dashboard';
  const nextRef = useRef(next);

  // Animations
  const titleAnim = useFadeIn(100);
  const subtitleAnim = useFadeIn(200);
  const buttonsAnim = useFadeIn(300);
  const dividerAnim = useFadeIn(400);
  const inputAnim = useFadeIn(450);
  const footerAnim = useFadeIn(550);

  const handleGoogleSignIn = async () => {
    console.warn('[LoginPage] handleGoogleSignIn fired, isLoading:', isLoading);
    setError('');
    setIsLoading(true);
    try {
      await signInWithGoogle();
      console.warn('[LoginPage] Google sign-in resolved OK');
    } catch (err: any) {
      const code = err?.code || 'unknown';
      const msg = err?.message || String(err);
      console.warn('[LoginPage] Google sign-in error:', code, msg, err);
      setError(import.meta.env.DEV ? `[${code}] ${msg}` : getAuthErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    console.warn('[LoginPage] handleAppleSignIn fired');
    setError('');
    setIsLoading(true);
    try {
      await signInWithApple();
      console.warn('[LoginPage] Apple sign-in resolved OK');
    } catch (err: any) {
      const code = err?.code || 'unknown';
      const msg = err?.message || String(err);
      console.warn('[LoginPage] Apple sign-in error:', code, msg, err);
      setError(import.meta.env.DEV ? `[${code}] ${msg}` : getAuthErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailContinue = () => {
    if (!email || !email.includes('@')) {
      setError('Enter a valid email address');
      return;
    }
    navigate(`/login/email?email=${encodeURIComponent(email)}&next=${encodeURIComponent(nextRef.current)}`);
  };

  // Post-login redirect — profile is only non-null for authenticated users.
  // Fires on both popup (profile set in same lifecycle) and redirect (profile
  // set by getRedirectResult/onAuthStateChanged after page reload).
  useEffect(() => {
    if (profile) {
      if (!profile.onboardingCompleted) {
        navigate(`/onboarding?next=${encodeURIComponent(nextRef.current)}`, { replace: true });
      } else {
        const postAuthAction = sessionStorage.getItem('postAuthAction');
        const destination = postAuthAction === 'save' ? '/app/result' : nextRef.current;
        if (postAuthAction) sessionStorage.removeItem('postAuthAction');
        navigate(destination, { replace: true });
      }
    }
  }, [profile, navigate]);

  const inputBase: React.CSSProperties = {
    width: '100%',
    height: 52,
    borderRadius: 12,
    border: `1.5px solid ${T.border}`,
    backgroundColor: T.white,
    padding: '0 16px',
    fontFamily: BRAND.fontSans,
    fontSize: 14,
    color: BRAND.ink,
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 200ms ease',
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = BRAND.teal;
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = T.border;
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: BRAND.cream,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar — back arrow */}
      <div
        style={{
          padding: '16px 20px',
          paddingTop: 'max(16px, env(safe-area-inset-top))',
        }}
      >
        <button
          onClick={() => navigate('/signup')}
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          padding: '0 24px',
          paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
          maxWidth: 420,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <h1
          style={{
            ...titleAnim,
            fontFamily: BRAND.fontSans,
            fontSize: 26,
            fontWeight: 700,
            color: BRAND.ink,
            textAlign: 'center',
            marginBottom: 8,
            letterSpacing: '-0.02em',
          }}
        >
          Welcome back
        </h1>

        {/* Subtitle */}
        <p
          style={{
            ...subtitleAnim,
            fontFamily: BRAND.fontSans,
            fontSize: 14,
            color: BRAND.inkLight,
            textAlign: 'center',
            marginBottom: 32,
            lineHeight: 1.5,
          }}
        >
          Sign in to access your pricing data.
        </p>

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: 'rgba(214,69,69,0.1)',
              borderRadius: 12,
              fontSize: 13,
              color: T.danger,
              textAlign: 'center',
              fontFamily: BRAND.fontSans,
            }}
          >
            {error}
          </div>
        )}

        {/* Apple — primary */}
        <div style={buttonsAnim}>
          <button
            onClick={handleAppleSignIn}
            disabled={isLoading}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 12,
              border: 'none',
              backgroundColor: BRAND.ink,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              fontFamily: BRAND.fontSans,
              fontSize: 14,
              fontWeight: 600,
              color: T.white,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              marginBottom: 10,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Sign in with Apple
          </button>

          {/* Google — secondary */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 12,
              border: `1.5px solid ${T.border}`,
              backgroundColor: T.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              fontFamily: BRAND.fontSans,
              fontSize: 14,
              fontWeight: 600,
              color: BRAND.ink,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>

        {/* OR divider */}
        <div
          style={{
            ...dividerAnim,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            margin: '24px 0',
          }}
        >
          <div style={{ flex: 1, height: 1, backgroundColor: T.border }} />
          <span
            style={{
              fontFamily: BRAND.fontSans,
              fontSize: 11,
              fontWeight: 600,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            or enter your email
          </span>
          <div style={{ flex: 1, height: 1, backgroundColor: T.border }} />
        </div>

        {/* Email input */}
        <div style={inputAnim}>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleEmailContinue(); }}
            onFocus={onFocus}
            onBlur={onBlur}
            style={{ ...inputBase, marginBottom: 16 }}
            autoComplete="email"
          />

          {/* Continue button */}
          <button
            onClick={handleEmailContinue}
            disabled={!email || isLoading}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 12,
              border: 'none',
              backgroundColor: email ? BRAND.teal : T.border,
              fontFamily: BRAND.fontSans,
              fontSize: 14,
              fontWeight: 600,
              color: email ? T.white : T.textMuted,
              cursor: email ? 'pointer' : 'not-allowed',
              transition: 'all 200ms ease',
            }}
          >
            Continue
          </button>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Footer */}
        <p
          style={{
            ...footerAnim,
            textAlign: 'center',
            fontFamily: BRAND.fontSans,
            fontSize: 13,
            color: BRAND.inkLight,
            marginTop: 24,
            marginBottom: 0,
          }}
        >
          Don't have an account?{' '}
          <button
            onClick={() => navigate('/signup')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontFamily: BRAND.fontSans,
              color: BRAND.teal,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Sign up &rarr;
          </button>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
